import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Heart, Eye, MessageCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import DOMPurify from 'dompurify';
import '../styles/StoryPreview.css';

function StoryPreview() {
  const location = useLocation();
  const navigate = useNavigate();
  const { title, authorName, storyId, chapterId } = location.state || {};

  const [publishing, setPublishing] = useState(false);
  const [likes, setLikes] = useState(0);
  const [views, setViews] = useState(0);
  const [commentsCount, setCommentsCount] = useState(0);
  const [currentChapter, setCurrentChapter] = useState(chapterId || 1);
  const [chapters, setChapters] = useState([]);
  const [currentChapterData, setCurrentChapterData] = useState({});
  const [author, setAuthor] = useState(authorName);
  const [showChapters, setShowChapters] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  useEffect(() => {
    if (!storyId || !chapterId) {
      console.error('Hikaye ID veya Bölüm ID eksik!');
      navigate('/');
      return;
    }

    const fetchStoryWithChapters = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get(`/preview/books/${storyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { chapters, author_name } = response.data;
        chapters.sort((a, b) => a.chapter_number - b.chapter_number);
        setChapters(chapters);
        setAuthor(author_name);

        const targetChapter = chapters.find((c) => c.chapter_id === chapterId);
        if (targetChapter) {
          setCurrentChapterData(targetChapter);
          setCurrentChapter(targetChapter.chapter_number);
          setLikes(targetChapter.likes);
          setViews(targetChapter.views);
          setCommentsCount(targetChapter.comments || 0);
        }
      } catch (error) {
        console.error('Error fetching story and chapters:', error);
      }
    };

    fetchStoryWithChapters();
  }, [chapterId, navigate, storyId]);

  const togglePublish = () => {
    setModalMessage(
      currentChapterData.is_published
        ? 'Bu bölümü yayından kaldırmak istediğinize emin misiniz?'
        : 'Bu bölümü yayımlamak istediğinize emin misiniz?'
    );
    setModalOpen(true);
  };

  const publishChapter = async (chapter_id, storyId) => {
    const token = localStorage.getItem('token');
    await api.post(
      `/preview/chapters/${chapter_id}/publish`,
      { book_id: storyId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const unpublishChapter = async (chapter_id, storyId) => {
    const token = localStorage.getItem('token');
    await api.post(
      `/preview/chapters/${chapter_id}/unpublish`,
      { book_id: storyId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const handleConfirm = async () => {
    setModalOpen(false);
    const { chapter_id, is_published } = currentChapterData;

    try {
      if (is_published) {
        await unpublishChapter(chapter_id, storyId);
      } else {
        await publishChapter(chapter_id, storyId);
      }

      setCurrentChapterData({
        ...currentChapterData,
        is_published: !is_published,
      });
      navigate('/my-stories');
    } catch (error) {
      console.error(`Error during ${is_published ? 'unpublishing' : 'publishing'}:`, error);
    }
  };

  const toggleChapters = () => {
    setShowChapters((prev) => !prev);
  };

  const changeChapter = (direction) => {
    const currentIndex = chapters.findIndex((c) => c.chapter_number === currentChapter);
    const newChapterIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newChapterIndex >= 0 && newChapterIndex < chapters.length) {
      const newChapter = chapters[newChapterIndex];
      setCurrentChapterData(newChapter);
      setCurrentChapter(newChapter.chapter_number);
      setLikes(newChapter.likes);
      setViews(newChapter.views);
      setCommentsCount(newChapter.comments || 0);
    }
  };

  const handleChapterClick = (chapterNumber) => {
    const chapter = chapters.find((c) => c.chapter_number === chapterNumber);
    if (chapter) {
      setCurrentChapterData(chapter);
      setCurrentChapter(chapter.chapter_number);
      setLikes(chapter.likes);
      setViews(chapter.views);
      setCommentsCount(chapter.comments || 0);
    }
  };

  return (
    <div className="story-preview-container">
      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <p>{modalMessage}</p>
            <div className="modal-buttons">
              <button onClick={handleConfirm}>Evet</button>
              <button onClick={() => setModalOpen(false)}>Hayır</button>
            </div>
          </div>
        </div>
      )}
      <div className="header-section">
        <div className="button-strip">
          <div className="book-header" onClick={toggleChapters}>
            <div className="book-details">
              <span className="book-title">{currentChapterData?.title || title || 'Kitap Başlığı'}</span>
              <span className="book-author">{author} tarafından</span>
            </div>
            <span className="dropdown-arrow">{showChapters ? '▲' : '▼'}</span>
          </div>
          <div className="buttons">
            <button className="publish-button" onClick={togglePublish} disabled={publishing}>
              {publishing
                ? 'Yayımlanıyor...'
                : currentChapterData.is_published
                ? 'Yayımdan Kaldır'
                : 'Yayımla'}
            </button>
            <button className="back-to-edit" onClick={() => navigate(-1)}>
              Düzenlemeye geri dön
            </button>
          </div>
        </div>
        {showChapters && (
          <div className="chapter-list">
            {chapters.map((chapter) => (
              <div
                key={chapter.chapter_id}
                className={`chapter-item ${currentChapter === chapter.chapter_number ? 'active' : ''}`}
                onClick={() => handleChapterClick(chapter.chapter_number)}
              >
                <span>{chapter.title}</span>
                <span>{chapter.is_published ? 'Yayımlanmış' : 'Taslak'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="story-preview-main">
        <div className="story-info">
          <h1>{currentChapterData?.title || title}</h1>
          <div className="story-stats">
            <span>
              <Eye size={16} /> {views}
            </span>
            <span>
              <Heart size={16} /> {likes}
            </span>
            <span>
              <MessageCircle size={16} /> {commentsCount}
            </span>
          </div>
        </div>
        <div
          className="story-content"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(currentChapterData?.content || 'Hikaye içeriği buraya gelecek...'),
          }}
        />
        <div className="chapter-navigation">
          <button onClick={() => changeChapter('prev')} disabled={currentChapter === chapters[0]?.chapter_number}>
            <ArrowLeft size={20} /> Önceki Bölüm
          </button>
          <button
            onClick={() => changeChapter('next')}
            disabled={currentChapter === chapters[chapters.length - 1]?.chapter_number}
          >
            Sonraki Bölüm <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default StoryPreview;
